import { CommonModule } from '@angular/common';
import { Component, inject, Inject, Input, OnInit } from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { ApiSimplePropertyRuleset } from '@app/features/property-rulesets/property-ruleset.interface';
import { PropertyRulesetService } from '@app/features/property-rulesets/property-ruleset.service';
import { IntercomService } from '@app/shared/services/intercom.service';
import { SegmentEvent, SegmentIoService } from '@app/shared/services/segmentIo/segment-io.service';
import { sleep } from '@app/shared/utils';
import { UiModule } from '@app/ui';
import { CloseComponent, Icon, IconComponent, LoadingSpinnerComponent } from '@ui';
import { BehaviorSubject, combineLatest, delay, filter, map } from 'rxjs';

interface RuleSetsImportDialogData {
  rulesets: ApiSimplePropertyRuleset[];
}

enum RuleSetsImportStep {
  INITIAL = 0,
  PREVIEW = 1,
  IMPORTING = 2,
  DONE = 3,
}

@Component({
  standalone: true,
  selector: 'sbnb-rule-sets-import-modal',
  templateUrl: './rule-sets-import-modal.component.html',
  styleUrls: ['./rule-sets-import-modal.component.scss'],
  imports: [CommonModule, UiModule, IconComponent, LoadingSpinnerComponent, CloseComponent],
  providers: [],
})
export class RuleSetsImportModalComponent implements OnInit {
  public step: RuleSetsImportStep = RuleSetsImportStep.INITIAL;
  public RuleSetsImportStep = RuleSetsImportStep;
  private propertyRulesetService = inject(PropertyRulesetService);
  private segmentIoService = inject(SegmentIoService);
  private intercomService = inject(IntercomService);

  public airbnbRuleSets: ApiSimplePropertyRuleset[] = [];
  public rulesetsImportSteps: { icon: Icon; description: string }[] = [
    { icon: 'cloud-download', description: 'Importing Airbnb rule-sets...' },
    { icon: 'refresh', description: 'Re-creating rule-sets in Hospitable...' },
    { icon: 'bin', description: 'Clearing Airbnb rule-sets...' },
  ];

  @Input() public startingStep: RuleSetsImportStep = RuleSetsImportStep.INITIAL;

  public importError$ = new BehaviorSubject<boolean>(false);
  public importProgress$ = new BehaviorSubject<number>(0);
  public done$ = this.importProgress$.pipe(
    filter((progress) => progress === 3),
    delay(2000),
    map(() => true)
  );

  public previewRuleSets: ApiSimplePropertyRuleset[] = [];

  constructor(
    public dialogRef: MatDialogRef<RuleSetsImportModalComponent>,
    @Inject(MAT_DIALOG_DATA)
    public dialogData: RuleSetsImportDialogData
  ) {}

  public ngOnInit() {
    if (this.startingStep) {
      this.step = this.startingStep;
    }
    this.retrieveAirbnbRuleSets();
  }

  public moveForward() {
    this.step++;
  }

  public stepBack() {
    if (this.step === this.startingStep) {
      this.closeModal();
      return;
    }
    this.step--;
  }

  public async startImport() {
    this.moveForward();
    this.importProgress$.next(1);
    for (let i = 1; i < 6; i++) {
      await sleep(1500);
      this.importProgress$.next(i);
    }
  }

  public closeModal(success = false) {
    this.dialogRef.close(success);
  }

  public isProgressLessThan(n: number, progress: number | null): boolean {
    return (progress ?? 0) < n;
  }

  public retrieveAirbnbRuleSets() {
    this.airbnbRuleSets = this.dialogData.rulesets?.filter((ruleset) => ruleset.title);
    this.previewRuleSets = this.airbnbRuleSets.slice(0, 3);
  }

  public optIn(importsInitialRulesets: boolean) {
    this.segmentIoService.track(
      importsInitialRulesets ? SegmentEvent.RuleSetsOptInWithImport : SegmentEvent.RuleSetsOptInWithoutImport,
      {}
    );
    this.startImport();
    try {
      combineLatest([this.propertyRulesetService.optIn(importsInitialRulesets), this.importProgress$]).subscribe(
        ([optIn, progress]) => {
          if (progress === 5 && optIn) {
            this.propertyRulesetService.rulesetsUpdated$.next();
            this.step = RuleSetsImportStep.DONE;
            this.segmentIoService.track(SegmentEvent.RuleSetsOptInSuccess, {});
          }
        }
      );
    } catch (error) {
      this.segmentIoService.track(SegmentEvent.RuleSetsOptInError, {
        error: error.message,
      });
      this.step = RuleSetsImportStep.DONE;
      this.importError$.next(true);
    }
  }

  public contactSupport() {
    this.segmentIoService.track(SegmentEvent.RuleSetsOptInContactSupport, {});
    this.intercomService.show();
    this.closeModal();
  }
}
